import { TCurrencyCode } from '@llws/hydra-shared';

/**
 * The CSV header a mass credit upload should use.
 *
 * A subscription is routinely billed for many buildings at once — one row of the July 2026
 * batch covers 188 — so a row carries a LIST of building ids, and optionally the matching
 * names, rather than a single id.
 */
export const MASS_CREDIT_CSV_HEADER = 'amount,buildingIds,buildingNames,subscriptionId';

/**
 * The original single-building header, still accepted.
 *
 * The UI upload modal has always produced this shape and there is no reason to break it: a
 * row parsed from it simply yields a one-element building list and no names. New callers
 * should use {@link MASS_CREDIT_CSV_HEADER}.
 */
export const MASS_CREDIT_LEGACY_CSV_HEADER = 'amount,buildingId,subscriptionId';

/** Both accepted header shapes, newest first. An upload must match one of them exactly. */
export const MASS_CREDIT_CSV_HEADERS = [
  MASS_CREDIT_CSV_HEADER,
  MASS_CREDIT_LEGACY_CSV_HEADER,
] as const;

/**
 * What separates ids inside a `buildingIds` cell, and names inside a `buildingNames` cell.
 *
 * They differ, and deliberately so — this is the source sheet's own convention. Building
 * names contain spaces and commas, so only a character that cannot appear in an id list is
 * safe for them.
 */
export const MASS_CREDIT_BUILDING_ID_SEPARATOR = ' ';
export const MASS_CREDIT_BUILDING_NAME_SEPARATOR = '|';

/**
 * How many buildings a description will name before it switches to counting them.
 *
 * Three is a readable list; the July 2026 batch has rows covering 188 buildings, and naming
 * those would be neither readable nor storable. Lives here rather than beside the description
 * builder because the upload preview has to render by the same rule — a preview that
 * disagreed with the description it is previewing would be worse than no preview.
 */
export const MASS_CREDIT_MAX_NAMED_BUILDINGS = 3;

/**
 * The most characters a credit description may occupy.
 *
 * `credits.description` is a `varchar` whose width is not declared on the entity
 * (`entities/hydra/Credit.ts:76`), and TypeORM's implied default — 255 — is the safe floor.
 * Xero's own line-item description limit (4000) is far looser, so this is the binding one.
 * Named here so raising it is a one-line change once the live column width is confirmed.
 */
export const MASS_CREDIT_DESCRIPTION_MAX_LENGTH = 255;

/**
 * The shape of the charge period every mass credit upload must name: `YYYY-MM`.
 *
 * The period is what finds the charge, and therefore the invoice, each credit comes off — so
 * it is required rather than defaulted. Guessing it would guess linkage, which is the one
 * thing this feature refuses to do. Shared so the upload modal can disable its own submit on
 * the same rule the server enforces.
 */
export const MASS_CREDIT_PERIOD_PATTERN = /^(\d{4})-(0[1-9]|1[0-2])$/;

/** The multipart field name the mass credit CSV must be uploaded under. */
export const MASS_CREDIT_UPLOAD_FIELD = 'massCreditUpload';

/** A single successfully parsed CSV row. `lineNumber` is 1-based, with the header as line 1. */
export interface IMassCreditCsvRow {
  lineNumber: number;
  amount: number;
  /**
   * Every building this row credits, in source order. Always at least one; a legacy
   * single-building row yields a one-element list.
   */
  buildingIds: number[];
  /**
   * The names supplied alongside {@link buildingIds}, in the same order — empty when the
   * upload carried none.
   *
   * NOT guaranteed to be the same length as `buildingIds`: a building name may itself contain
   * the name separator, which over-splits the cell. Callers must check the two lengths agree
   * before pairing them off, and fall back to a database lookup when they do not.
   */
  buildingNames: string[];
  subscriptionId: number;
}

/** A problem found with one CSV row. Errors exclude the row; warnings do not. */
export interface IMassCreditRowIssue {
  lineNumber: number;
  message: string;
  rawRow?: string;
}

/**
 * One credit line of a planned credit note — always exactly one CSV row, so that the
 * building a credit came from stays visible on the line it produced.
 */
export interface IMassCreditPlanLine {
  lineNumber: number;
  subscriptionId: number;
  /** Every building this line credits, carried whole from the CSV row. Never truncated. */
  buildingIds: number[];
  /**
   * Every building name that could be resolved, in `buildingIds` order. Empty when no
   * trustworthy set could be assembled. This is the FULL list — it is what goes into the
   * note's comments, and it is routinely longer than what the description says.
   */
  buildingNames: string[];
  /**
   * The building names actually used to write {@link description}, in the order they appear
   * in it. Empty when the line names a count rather than the buildings — so this is what was
   * *said*, whereas `buildingNames` is what is *known*.
   */
  namedBuildings: string[];
  clientId: number;
  amount: string;
  description: string;
  /**
   * The charge (`generated_subscriptions.id`) this credit came off, when exactly one
   * non-deleted charge existed for the subscription in the credited period.
   *
   * This is provenance, not application: it is what the credit note PDF follows
   * (charge -> `generated_subscriptions_has_taxes` -> invoice) to print "From invoices",
   * and what lets commission clawbacks be calculated. `null` whenever the charge could not
   * be resolved unambiguously — an unresolved link is left empty rather than guessed.
   */
  generatedSubscriptionId: number | null;
  /**
   * The invoice (`invoices.id`) this credit is drawn off — the same provenance link the bulk
   * subscription credit modal sets. A charge billed across several invoices produces one line
   * per invoice, and a row whose invoices could not be resolved is reported as a row error
   * rather than becoming an unlinked credit.
   *
   * `null` only when the caller does no invoice resolution at all. No user-facing path can be
   * that caller any more: both the upload preview and the create action require a credited
   * period, so an unlinked credit cannot be written from the UI.
   */
  invoiceId: number | null;
  /** `invoices.invoice_number`, as the credit-note modals send it. */
  invoiceNumber: string | null;
  /** `invoices.invoice_date` as `YYYY-MM-DD`. */
  invoiceDate: string | null;
  /** `invoices.ownership_group_id` — the note-level grouping key the splits endpoint uses. */
  ownershipGroupId: number | null;
  /**
   * The tax name, rate and amount for this credit, taken from the SAME
   * `generated_subscriptions_has_taxes` row the invoice link and subtotal came from, with the
   * amount computed on this line's apportioned share rather than copied from the charge.
   */
  taxName: string | null;
  taxRate: string;
  taxAmount: string;
  /** The charge's year and month, as the bulk credit modal sends them. */
  year: number | null;
  month: number | null;
}

/**
 * One planned credit note. Notes are keyed on
 * `(billingAccountId, currencyCode, ownershipGroupId, taxRate)` — the billing account and
 * currency because a note belongs to one account in one currency, and the ownership group and
 * tax rate because both are single-valued note-level columns and because that is exactly how
 * the credit-note list view's splits endpoint groups credits
 * (`routers/creditNotes.ts:478-490`).
 */
export interface IMassCreditNotePlan {
  billingAccountId: number;
  clientId: number;
  clientName: string;
  currencyCode: TCurrencyCode;
  noteDate: string;
  lines: IMassCreditPlanLine[];
  subscriptionIds: number[];
  subtotalAmount: string;
  /** Sum of every line's tax. The server recomputes this from the credits it is sent. */
  taxAmount: string;
  /** The note's tax name and rate — one per note, which is why they are part of the note key. */
  taxName: string | null;
  taxRate: string | null;
  /** The invoices' ownership group, which the splits endpoint also keys notes on. */
  ownershipGroupId: number | null;
  /** `subtotalAmount` plus `taxAmount`. */
  totalAmount: string;
  /**
   * Every distinct lift client contributing lines to this note. More than one is legal
   * (several clients can share a billing account) but is surfaced so a reviewer can see
   * that the note's client-level fields come from the first line only.
   */
  clientIds: number[];
}

/** The full, reviewable result of parsing an upload. Producing it writes nothing. */
export interface IMassCreditPlan {
  /** Stable digest of the normalised CSV, used to detect a re-upload of the same file. */
  batchKey: string;
  /** The normalised CSV the plan was built from; replayed verbatim on create. */
  csvText: string;
  /**
   * Whether this plan was built by a caller that resolved invoices. When `true`, every credit
   * must name the invoice it comes off and no write path will accept one that does not.
   *
   * Always `true` for a plan built from the UI, which requires a credited period. `false` is
   * reachable only by an internal caller that supplies no period at all.
   */
  invoiceLinkageRequired: boolean;
  notes: IMassCreditNotePlan[];
  rowErrors: IMassCreditRowIssue[];
  warnings: IMassCreditRowIssue[];
  totals: {
    rowCount: number;
    noteCount: number;
    amountByCurrency: { currencyCode: TCurrencyCode, totalAmount: string }[];
  };
}

/** An already-created credit note carrying the batch marker for this upload. */
export interface IMassCreditExistingNote {
  id: number;
  noteNumber: string;
}

export interface IMassCreditPreviewResponse {
  status: 'success' | 'error';
  message: string;
  plan: IMassCreditPlan | null;
  /** Non-empty when this exact CSV has already been used to create notes. */
  existingBatchNotes: IMassCreditExistingNote[];
}

export interface IMassCreditCreatedNote {
  id: number;
  noteNumber: string;
  billingAccountId: number;
  clientName: string;
  currencyCode: TCurrencyCode;
  totalAmount: string;
  lineCount: number;
}

/** The note that could not be created, and why. */
export interface IMassCreditCreateFailure {
  billingAccountId: number;
  clientName: string;
  currencyCode: TCurrencyCode;
  message: string;
}

export interface IMassCreditCreateResponse {
  status: 'success' | 'error';
  message: string;
  /**
   * Notes that were created. Non-empty alongside an `error` status when a failure stopped a
   * run partway: each note is its own transaction, so earlier notes survive.
   */
  createdNotes: IMassCreditCreatedNote[];
}
